import { Link } from "react-router-dom";
import { ArrowUpRight, Linkedin, Mail, Phone } from "lucide-react";
import Logo from "@/components/Logo";
import XLogo from "@/components/XLogo";
import { SERVICES, CONTACT_EMAIL, PHONE_DISPLAY, PHONE_TEL, SOCIAL_LINKS, mailto } from "@/data/site";

const Footer = () => {
  const year = new Date().getFullYear();
  return (
    <footer className="noise relative overflow-hidden bg-night text-white" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-6 pt-20 md:pt-28">
        <div className="grid gap-14 pb-20 md:grid-cols-12">
          <div className="md:col-span-5">
            <Logo light />
            <p className="mt-4 font-display text-[13px] font-semibold uppercase tracking-[0.24em] text-white">
              Modern Challenges, Innovative Solutions
            </p>
            <p className="mt-5 max-w-sm text-[15px] leading-relaxed text-white/60">
              Build, deploy and secure your digital infrastructure. TecKube brings cloud,
              containers, applications and security into one service offering.
            </p>
            <div className="mt-8 flex flex-col items-start gap-3">
              <a
                href={mailto("TecKube enquiry")}
                data-testid="footer-email-link"
                className="inline-flex items-center gap-2 font-mono text-sm text-white/80 transition-colors duration-300 hover:text-white"
              >
                <Mail className="h-4 w-4" />
                {CONTACT_EMAIL}
              </a>
              <a
                href={`tel:${PHONE_TEL}`}
                data-testid="footer-phone-link"
                className="inline-flex items-center gap-2 font-mono text-sm text-white/80 transition-colors duration-300 hover:text-white"
              >
                <Phone className="h-4 w-4" />
                {PHONE_DISPLAY}
              </a>
            </div>
            <div className="mt-7 flex items-center gap-3">
              {SOCIAL_LINKS.map((s) => (
                <a
                  key={s.id}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`footer-${s.id}-link`}
                  aria-label={`TecKube on ${s.label}`}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white/70 transition-all duration-300 hover:-translate-y-0.5 hover:border-primary hover:bg-primary hover:text-white"
                >
                  {s.id === "linkedin" ? <Linkedin className="h-4 w-4" /> : <XLogo />}
                </a>
              ))}
            </div>
          </div>

          <div className="md:col-span-3">
            <p className="text-[11px] font-medium uppercase tracking-[0.28em] text-white/40">
              Services
            </p>
            <ul className="mt-6 space-y-3.5">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/services/${s.slug}`}
                    data-testid={`footer-service-${s.slug}-link`}
                    className="text-[15px] text-white/65 transition-colors duration-300 hover:text-white"
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="text-[11px] font-medium uppercase tracking-[0.28em] text-white/40">
              Company
            </p>
            <ul className="mt-6 space-y-3.5">
              <li>
                <Link
                  to="/academy"
                  data-testid="footer-academy-link"
                  className="text-[15px] text-white/65 transition-colors duration-300 hover:text-white"
                >
                  TecKube Academy
                </Link>
              </li>
              <li>
                <Link
                  to="/about"
                  data-testid="footer-about-link"
                  className="text-[15px] text-white/65 transition-colors duration-300 hover:text-white"
                >
                  About
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  data-testid="footer-contact-link"
                  className="text-[15px] text-white/65 transition-colors duration-300 hover:text-white"
                >
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="text-[11px] font-medium uppercase tracking-[0.28em] text-white/40">
              Start
            </p>
            <p className="mt-6 text-[15px] leading-relaxed text-white/65">
              Tell us what you need to build or improve.
            </p>
            <Link
              to="/contact"
              data-testid="footer-start-link"
              className="mt-5 inline-flex items-center gap-2 font-display text-[15px] font-semibold text-white"
            >
              Contact TecKube
              <ArrowUpRight className="h-4 w-4 text-primary-soft" />
            </Link>
          </div>
        </div>

        <div
          aria-hidden="true"
          className="select-none overflow-hidden border-t border-white/10 pt-8"
        >
          <div className="whitespace-nowrap text-center font-display text-[clamp(4rem,17.5vw,16rem)] font-bold leading-[0.82] tracking-tightest text-white/[0.06]">
            TECKUBE
          </div>
        </div>

        <div className="flex flex-col items-center justify-between gap-3 border-t border-white/10 py-7 text-[13px] text-white/40 md:flex-row">
          <span data-testid="footer-copyright">© {year} TecKube. All rights reserved.</span>
          <span className="flex items-center gap-5">
            <Link
              to="/terms"
              data-testid="footer-terms-link"
              className="transition-colors duration-300 hover:text-white"
            >
              Terms of Use
            </Link>
            <Link
              to="/privacy"
              data-testid="footer-privacy-link"
              className="transition-colors duration-300 hover:text-white"
            >
              Privacy Policy
            </Link>
            <span>Cloud · DevOps · Applications · Cybersecurity</span>
          </span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
